import { Add, DeleteOutline, Groups, Person } from '@mui/icons-material';
import { Button, Grid, IconButton, List, ListItem, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { cancelExtraMembership } from 'src/shared/services/extraMembershipService';
import { useExtraMembershipLabel } from '../hooks/useExtraMembershipLabel';
import { ExtraMembership } from '../models/extraMembership';
import { Member } from '../models/member';
import { MemberExtraMembershipCreateModal } from './memberExtraMembershipCreateModal';

type MemberExtraMembershipsProps = {
  member: Member;
  showEditButtons?: boolean | undefined;
  refetchMemberCallback: () => void;
};

/**
 * Vänmedlemskap på medlemsbilden, åt båda håll. På det ordinarie medlemskapet listas
 * vänmedlemskapen i andra föreningar, och på en vänpost visas vilket ordinarie medlemskap posten
 * hör till.
 */
export const MemberExtraMemberships = (props: MemberExtraMembershipsProps) => {
  const { member, showEditButtons = true, refetchMemberCallback } = props;
  const { t } = useTranslation(['member']);
  const { currentUser, currentMember } = useGlobals();
  const navigate = useNavigate();

  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [cancelItem, setCancelItem] = useState<ExtraMembership | undefined>(undefined);
  const [isWorking, setIsWorking] = useState<boolean>(false);

  const enabled = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.UseNewFriend, 'False') === 'True';
  const label = useExtraMembershipLabel();

  const hasMemberUpdatePermission = getPermission(currentUser, PermissionEnum.MemberUpdate);

  const extraMemberships = member.extraMemberships ?? [];
  const parent = member.parentMembership;

  // Efter att posten skapats i adminvyn går handläggaren vidare till den nya medlemmen med
  // redigeravyn öppen, i stället för att behöva leta upp posten själv.
  const openCreated = (createdMemberId: number) => {
    navigate(`${RoutesEnum.MemberDetails}/${createdMemberId}?edit=1`);
  };

  const cancel = async () => {
    if (!cancelItem) return;

    setIsWorking(true);

    try {
      await cancelExtraMembership(cancelItem.id);
      toast.success(<>{t('member:extraMembership.cancelSuccess', { label })}</>);
      setCancelItem(undefined);
      refetchMemberCallback();
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(<>{t('member:extraMembership.cancelError', { label: label.toLowerCase() })}</>);
    } finally {
      setIsWorking(false);
    }
  };

  // Funktionen är avstängd, eller så är posten varken kopplad eller kan kopplas.
  if (!enabled) return null;

  // För medlemmar på Min sida visas rutan bara när det faktiskt finns något att visa.
  if (currentMember && extraMemberships.length === 0 && !parent) return null;

  return (
    <Grid item xs={12}>
      <ContentSection
        header={label as string}
        showHeaderCounter={extraMemberships.length > 0}
        headerCounter={extraMemberships.length}
        button={
          <>
            {(currentUser || currentMember) && showEditButtons && !parent && (
              <Button
                disabled={!!currentUser && !hasMemberUpdatePermission}
                variant='outlined'
                size='small'
                onClick={() => setOpenCreateModal(true)}
                endIcon={<Add fontSize='small' />}
              >
                {t('common:button.create')}
              </Button>
            )}
          </>
        }
      >
        <List dense>
          {parent && (
            <ListItem sx={{ py: 0, pl: 0.5 }}>
              <ListItemIcon sx={{ width: '2rem', maxWidth: '2rem', minWidth: '2rem' }}>
                <Person fontSize='small' />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    {currentUser ? (
                      <Link to={`${RoutesEnum.MemberDetails}/${parent.memberId}`}>{`${parent.firstName} ${parent.lastName}`}</Link>
                    ) : (
                      <Typography component='span'>{`${parent.firstName} ${parent.lastName}`}</Typography>
                    )}
                    <Typography component='span' variant='caption' sx={{ fontWeight: 500, ml: 0.5 }}>
                      ({t('member:extraMembership.ordinaryIn', { association: parent.localAssName })})
                    </Typography>
                  </>
                }
              />
            </ListItem>
          )}

          {extraMemberships.map((item) => (
            <ListItem
              sx={{ py: 0, pl: 0.5 }}
              key={item.id}
              secondaryAction={
                (currentUser || currentMember) && showEditButtons && !item.resDate ? (
                  <Tooltip title={t('member:extraMembership.cancel', { label: label.toLowerCase() }) as string}>
                    <span>
                      <IconButton
                        size='small'
                        color='error'
                        disabled={!!currentUser && !hasMemberUpdatePermission}
                        onClick={() => setCancelItem(item)}
                      >
                        <DeleteOutline fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null
              }
            >
              <ListItemIcon sx={{ width: '2rem', maxWidth: '2rem', minWidth: '2rem' }}>
                <Groups fontSize='small' />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    {currentUser ? (
                      <Link to={`${RoutesEnum.MemberDetails}/${item.memberId}`}>{item.localAssName}</Link>
                    ) : (
                      <Typography component='span'>{item.localAssName}</Typography>
                    )}
                    {!!item.memberNr && (
                      <Typography component='span' variant='caption' sx={{ fontWeight: 500, ml: 0.5 }}>
                        ({t('member:extraMembership.memberNr', { memberNr: item.memberNr })})
                      </Typography>
                    )}
                    {!!item.resDate && (
                      <Typography component='span' variant='caption' sx={{ ml: 0.5, color: 'text.secondary' }}>
                        ({t('member:extraMembership.ended')})
                      </Typography>
                    )}
                  </>
                }
              />
            </ListItem>
          ))}

          {extraMemberships.length === 0 && !parent && (
            <ListItem sx={{ py: 0, pl: 0.5 }}>
              <ListItemText
                primary={
                  <Typography variant='body2' color='text.secondary'>
                    {t('member:extraMembership.none')}
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </ContentSection>

      {openCreateModal && (
        <MemberExtraMembershipCreateModal
          modalOpen={openCreateModal}
          memberId={member.member_id ?? 0}
          createInvoice={!!currentMember}
          onClose={() => setOpenCreateModal(false)}
          refetchMemberCallback={refetchMemberCallback}
          onCreated={currentUser ? openCreated : undefined}
        />
      )}

      <GenericDialog
        open={!!cancelItem}
        maxWidth='sm'
        dialogTitle={t('member:extraMembership.cancelTitle', { label: label.toLowerCase() })}
        dialogContent={
          <Typography variant='body2'>{t('member:extraMembership.cancelConfirm', { label, association: cancelItem?.localAssName })}</Typography>
        }
        saveLabel='button.confirm'
        onSave={cancel}
        onClose={() => {
          if (!isWorking) setCancelItem(undefined);
        }}
      />
    </Grid>
  );
};
